import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileDown,
  Loader2,
  Pencil,
  Send,
  CheckCircle2,
  XCircle,
  Trash2,
  ShoppingCart,
  Building2,
  Mail,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SupplierOrderFormDialog from "@/components/SupplierOrderFormDialog";
import {
  StatusBadge,
  STATUS_LABELS,
  formatFt,
  formatDate,
} from "@/pages/SupplierOrders";
import { openProtectedFile } from "@/components/employee/tabHelpers";

export default function SupplierOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [openSend, setOpenSend] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/supplier-orders/${id}`);
      setItem(data);
    } catch {
      toast.error("A rendelés nem található.");
      navigate("/supplier-orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const onDelete = async () => {
    if (!window.confirm("Biztosan törlöd ezt a vázlatot?")) return;
    try {
      await api.delete(`/supplier-orders/${id}`);
      toast.success("Vázlat törölve.");
      navigate("/supplier-orders");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  const onMarkDelivered = async () => {
    if (!window.confirm("Leszállítottnak jelölöd a rendelést?")) return;
    try {
      const { data } = await api.post(`/supplier-orders/${id}/mark-delivered`);
      setItem(data);
      toast.success("Rendelés leszállítva.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült frissíteni.");
    }
  };

  const onDownloadPdf = () => {
    openProtectedFile(
      `/supplier-orders/${id}/pdf`,
      "application/pdf",
      `rendeles-${item?.order_number || id}.pdf`
    );
  };

  if (loading || !item) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const isDraft = item.status === "draft";
  const isSent = item.status === "sent";

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="order-detail-page">
      <button
        onClick={() => navigate("/supplier-orders")}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Vissza a listához
      </button>

      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-foreground" />
              <h1 className="text-xl font-semibold tracking-tight">
                Beszállítói rendelés
              </h1>
              <StatusBadge status={item.status} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Azonosító:{" "}
              <span className="font-mono">{item.order_number || "—"}</span>
              {" · "}Rögzítette:{" "}
              <span className="font-medium text-foreground">{item.created_by_name}</span>
              {" · "}{formatDate(item.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onDownloadPdf}
              data-testid="download-order-pdf"
            >
              <FileDown className="mr-1.5 h-4 w-4" /> PDF letöltés
            </Button>
            {isDraft && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenEdit(true)}
                  data-testid="edit-order"
                >
                  <Pencil className="mr-1.5 h-4 w-4" /> Szerkesztés
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => setOpenSend(true)}
                  data-testid="open-send-dialog"
                >
                  <Send className="mr-1.5 h-4 w-4" /> Email küldése
                </Button>
              </>
            )}
            {isSent && (
              <Button
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={onMarkDelivered}
                data-testid="mark-delivered"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> Leszállítva
              </Button>
            )}
            {(isDraft || isSent) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpenCancel(true)}
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                data-testid="open-cancel-dialog"
              >
                <XCircle className="mr-1.5 h-4 w-4" /> Visszavonás
              </Button>
            )}
            {isDraft && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                data-testid="delete-order"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-3">
          <KV
            icon={Building2}
            label="Beszállító"
            value={item.supplier_name}
          />
          <KV
            icon={Building2}
            label="Telephely / Cég"
            value={
              <>
                {item.site_name || "—"}
                {item.company_name && (
                  <span className="ml-1 text-muted-foreground">
                    · {item.company_name}
                  </span>
                )}
              </>
            }
          />
          <KV
            icon={Mail}
            label="Cél e-mail cím"
            value={item.sent_to_email || "—"}
          />
        </div>

        {item.note && (
          <div className="mt-6 rounded-lg border border-border bg-muted/20 p-4">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Megjegyzés
            </p>
            <p className="whitespace-pre-wrap text-sm">{item.note}</p>
          </div>
        )}

        {item.status === "cancelled" && (
          <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50/40 p-4 text-sm dark:border-rose-500/30 dark:bg-rose-500/10">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
              Visszavonva · {formatDate(item.cancelled_at)} · {item.cancelled_by_name}
            </p>
            {item.cancelled_reason && (
              <p className="mt-1 whitespace-pre-wrap">{item.cancelled_reason}</p>
            )}
          </div>
        )}
      </section>

      {/* Items */}
      <section className="rounded-2xl border border-slate-200/70 bg-white p-0 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
        <header className="border-b border-border px-6 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Rendelési tételek
          </p>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-6 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  #
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  Termék
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em] whitespace-nowrap">
                  Menny.
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  Egység
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em] whitespace-nowrap">
                  Nettó egys.
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em]">
                  ÁFA
                </th>
                <th className="px-6 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em] whitespace-nowrap">
                  Nettó össz.
                </th>
              </tr>
            </thead>
            <tbody>
              {(item.items || []).map((it, idx) => {
                const lineNet = (Number(it.quantity) || 0) * (Number(it.net_price) || 0);
                return (
                  <tr
                    key={idx}
                    className="border-b border-border last:border-0"
                    data-testid={`detail-item-${idx}`}
                  >
                    <td className="px-6 py-3 text-muted-foreground">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium">{it.product_name}</span>
                      {it.kind === "free" && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                          szabad
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">{it.quantity}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{it.unit || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                      {formatFt(it.net_price)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {it.vat_rate}%
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums font-medium whitespace-nowrap">
                      {formatFt(lineNet)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="px-6 py-3 text-right text-muted-foreground whitespace-nowrap">
                  Nettó összesen
                </td>
                <td className="px-6 py-3 text-right tabular-nums font-medium whitespace-nowrap">
                  {formatFt(item.total_net)}
                </td>
              </tr>
              <tr>
                <td colSpan={6} className="px-6 py-3 text-right text-muted-foreground">
                  ÁFA
                </td>
                <td className="px-6 py-3 text-right tabular-nums whitespace-nowrap">
                  {formatFt(item.total_vat)}
                </td>
              </tr>
              <tr className="border-t border-border bg-muted/30">
                <td colSpan={6} className="px-6 py-4 text-right font-semibold whitespace-nowrap">
                  Bruttó összesen
                </td>
                <td
                  className="px-6 py-4 text-right tabular-nums text-base font-bold whitespace-nowrap"
                  data-testid="detail-total-gross"
                >
                  {formatFt(item.total_gross)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* Timeline */}
      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
        <p className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Idővonal
        </p>
        <ol className="space-y-3">
          {(item.timeline || []).map((ev) => (
            <li
              key={ev._id}
              className="flex items-start gap-3 text-sm"
              data-testid={`timeline-${ev._id}`}
            >
              <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-foreground/[0.06]">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="leading-snug">{ev.text}</p>
                <p className="text-[11px] text-muted-foreground">
                  {ev.author_name} · {formatDate(ev.created_at)}
                </p>
              </div>
            </li>
          ))}
          {(item.timeline || []).length === 0 && (
            <li className="text-sm text-muted-foreground">
              Még nincs esemény.
            </li>
          )}
        </ol>
      </section>

      <SupplierOrderFormDialog
        open={openEdit}
        onOpenChange={setOpenEdit}
        initial={item}
        onUpdated={(updated) => {
          setItem(updated);
          setOpenEdit(false);
        }}
      />

      <SendOrderDialog
        open={openSend}
        onOpenChange={setOpenSend}
        order={item}
        onSent={(updated) => {
          setItem(updated);
          setOpenSend(false);
        }}
      />

      <CancelOrderDialog
        open={openCancel}
        onOpenChange={setOpenCancel}
        orderId={id}
        onCancelled={(updated) => {
          setItem(updated);
          setOpenCancel(false);
        }}
      />
    </div>
  );
}

function KV({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <div className="mt-1 text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

function SendOrderDialog({ open, onOpenChange, order, onSent }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setEmail(order?.sent_to_email || "");
  }, [open, order]);

  const onConfirm = async () => {
    const target = (email || "").trim();
    if (!target || !target.includes("@")) {
      toast.error("Adj meg érvényes e-mail címet.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/supplier-orders/${order._id}/send`, {
        sent_to_email: target,
      });
      toast.success(`Rendelés elküldve: ${target}`);
      onSent?.(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Küldés sikertelen."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-order-dialog">
        <DialogHeader>
          <DialogTitle>Rendelés küldése</DialogTitle>
          <DialogDescription>
            A rendelés tartalma e-mailben és PDF mellékletként is elküldésre kerül a
            beszállítónak. A küldés után a rendelés tartalma már nem módosítható.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="send-email">Cél e-mail cím *</Label>
            <Input
              id="send-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="rendeles@beszallito.hu"
              data-testid="send-email-input"
            />
          </div>
          <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
            <p>
              <b>{order?.supplier_name}</b> részére, {order?.item_count || 0} tétel,
              bruttó <b>{formatFt(order?.total_gross)}</b>.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
            Mégse
          </Button>
          <Button
            onClick={onConfirm}
            disabled={sending}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="confirm-send-order"
          >
            {sending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            <Send className="mr-1.5 h-4 w-4" /> Küldés
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelOrderDialog({ open, onOpenChange, orderId, onCancelled }) {
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const onConfirm = async () => {
    if (!reason.trim()) {
      toast.error("Add meg a visszavonás indokát.");
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post(`/supplier-orders/${orderId}/cancel`, {
        reason: reason.trim(),
      });
      toast.success("Rendelés visszavonva.");
      onCancelled?.(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Visszavonás sikertelen."
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cancel-order-dialog">
        <DialogHeader>
          <DialogTitle>Rendelés visszavonása</DialogTitle>
          <DialogDescription>
            A visszavonás a rendelést véglegesen visszavontnak jelöli. Adj meg egy
            indokot, amely megjelenik az idővonalon.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label htmlFor="cancel-reason">Indok *</Label>
          <Textarea
            id="cancel-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Pl. beszállító visszamondta, nincs készleten…"
            data-testid="cancel-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={working}>
            Mégse
          </Button>
          <Button
            onClick={onConfirm}
            disabled={working}
            variant="destructive"
            data-testid="confirm-cancel-order"
          >
            {working && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            <XCircle className="mr-1.5 h-4 w-4" /> Visszavonás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export STATUS_LABELS for convenience if needed elsewhere
export { STATUS_LABELS };
